import { useCallback } from 'react';
import { useTranslate } from '@tolgee/react';
import useI18nScene, { IScene } from 'hooks/useI18nScene';

const RETURN_REFUND_RETRIEVE_MAP: Record<string, string> = {
  CANCELLED: 'scene.chatWorkspace.aiReception.reminder.returnRefundRetrieve.cancelled',
  CLOSED: 'scene.chatWorkspace.aiReception.reminder.returnRefundRetrieve.closed',
  ACCEPTED_OR_REFUND_PAID:
    'scene.chatWorkspace.aiReception.reminder.returnRefundRetrieve.acceptedOrRefundPaid',
};

const REFUND_RETRIEVE_MAP: Record<string, string> = {
  CANCELLED: 'scene.chatWorkspace.aiReception.reminder.refundRetrieve.cancelled',
  CLOSED: 'scene.chatWorkspace.aiReception.reminder.refundRetrieve.closed',
  ACCEPTED_OR_REFUND_PAID:
    'scene.chatWorkspace.aiReception.reminder.refundRetrieve.acceptedOrRefundPaid',
};

const CANCEL_ORDER_RETRIEVE_MAP: Record<string, string> = {
  NOT_IN_CANCEL_AND_NOT_CANCELLED:
    'scene.chatWorkspace.aiReception.reminder.cancelOrderRetrieve.notInCancelAndNotCancelled',
  CANCELLED: 'scene.chatWorkspace.aiReception.reminder.cancelOrderRetrieve.cancelled',
};

export default function useTipTrigger() {
  const { t } = useTranslate();
  const sceneName = useI18nScene();

  return useCallback(
    (scene: string, condition: string, waitReply?: boolean) => {
      if (
        [
          'REFUND',
          'SENT_MISTAKE',
          'PROBLEM_OF_PRODUCT',
          'RE_REMARK_SKU_REQ',
          'MODIFY_TRADE_REQ',
          'BARGAIN_MSG',
          'CANCEL_ORDER_REQ',
          'ITEM_CHANGE_REQ',
        ].includes(scene)
      ) {
        return t('scene.chatWorkspace.aiReception.reminder.followUp', {
          scene: sceneName[scene as keyof IScene],
        });
      }

      if (scene === 'VALUABLE_BUYER') {
        return t('domain.aiReception.type.valuableBuyer', {
          scene: sceneName[scene as keyof IScene],
        });
      }

      if (scene === 'RETURN_REFUND_RETRIEVE') {
        if (waitReply === true) {
          return t('scene.chatWorkspace.aiReception.reminder.returnRefundRetrieve.replied', {
            scene: sceneName[condition as keyof IScene],
          });
        }
        return t('scene.chatWorkspace.aiReception.reminder.returnRefundRetrieve.initial', {
          scene: sceneName[condition as keyof IScene],
        });
      }

      if (scene === 'REFUND_RETRIEVE') {
        if (waitReply === true) {
          return t('scene.chatWorkspace.aiReception.reminder.refundRetrieve.replied', {
            scene: sceneName[condition as keyof IScene],
          });
        }
        return t('scene.chatWorkspace.aiReception.reminder.refundRetrieve.initial', {
          scene: sceneName[condition as keyof IScene],
        });
      }

      if (scene === 'CANCEL_ORDER_RETRIEVE') {
        if (waitReply === true) {
          return t('scene.chatWorkspace.aiReception.reminder.cancelOrderRetrieve.replied', {
            scene: sceneName[condition as keyof IScene],
          });
        }
        return t('scene.chatWorkspace.aiReception.reminder.cancelOrderRetrieve.initial', {
          scene: sceneName[condition as keyof IScene],
        });
      }

      if (scene === 'NEGATIVE_RATE_RETRIEVE') {
        if (waitReply === true) {
          return t('scene.chatWorkspace.aiReception.reminder.negativeRateRetrieve.replied');
        }
        return t('scene.chatWorkspace.aiReception.reminder.negativeRateRetrieve.initial');
      }

      if (scene === 'TAX_REMIND') {
        return t('scene.chatWorkspace.aiReception.reminder.taxPaymentFollowUp');
      }

      if (scene === 'NEGATIVE_RATE_PRONE_BUYER') {
        return t('scene.chatWorkspace.currentConversation.buyerTag.negativeReviewReminder');
      }

      return '';
    },
    [t, sceneName],
  );
}

export function useTipManualClose() {
  const { t } = useTranslate();
  const sceneName = useI18nScene();

  return useCallback(
    (name: string, solved: boolean, trigger: Conversation.MsgSysRemindTrigger[]) => {
      if (solved) {
        if (trigger.length <= 1) {
          return t('scene.chatWorkspace.aiReception.reminder.manualClose.solvedOne', {
            name,
          });
        }
        return t('scene.chatWorkspace.aiReception.reminder.manualClose.solvedMore', {
          name,
          scene: `${sceneName[trigger[0].scene as keyof IScene]}${
            trigger[0].condition ? ` - ${sceneName[trigger[0].condition as keyof IScene]}` : ''
          }`,
        });
      }

      if (trigger.length <= 1) {
        return t('scene.chatWorkspace.aiReception.reminder.manualClose.noActionNeededOne', {
          name,
        });
      }
      return t('scene.chatWorkspace.aiReception.reminder.manualClose.noActionNeededMore', {
        name,
        scene: `${sceneName[trigger[0].scene as keyof IScene]}${
          trigger[0].condition ? ` - ${sceneName[trigger[0].condition as keyof IScene]}` : ''
        }`,
      });
    },
    [t, sceneName],
  );
}

export function useTipAutoClose() {
  const { t } = useTranslate();
  const sceneName = useI18nScene();

  return useCallback(
    (scene: string, timeout: boolean, condition: string, trigCond: string) => {
      if (
        timeout &&
        [
          'REFUND',
          'SENT_MISTAKE',
          'PROBLEM_OF_PRODUCT',
          'RE_REMARK_SKU_REQ',
          'MODIFY_TRADE_REQ',
          'BARGAIN_MSG',
          'CANCEL_ORDER_REQ',
          'ITEM_CHANGE_REQ',
          'VALUABLE_BUYER',
          'TAX_REMIND',
          'NEGATIVE_RATE_PRONE_BUYER',
        ].includes(scene)
      ) {
        return t('scene.chatWorkspace.aiReception.reminder.timeoutClose.default', {
          scene: sceneName[scene as keyof IScene],
        });
      }

      if (scene === 'RETURN_REFUND_RETRIEVE') {
        if (timeout) {
          return t('scene.chatWorkspace.aiReception.reminder.timeoutClose.returnRefundRetrieve', {
            scene: sceneName[trigCond as keyof IScene],
          });
        }
        if (['CANCELLED', 'CLOSED', 'ACCEPTED_OR_REFUND_PAID'].includes(condition)) {
          return t(RETURN_REFUND_RETRIEVE_MAP[condition], {
            scene: sceneName[trigCond as keyof IScene],
          });
        }
      }

      if (scene === 'REFUND_RETRIEVE') {
        if (timeout) {
          return t('scene.chatWorkspace.aiReception.reminder.timeoutClose.refundRetrieve', {
            scene: sceneName[trigCond as keyof IScene],
          });
        }
        if (['CANCELLED', 'CLOSED', 'ACCEPTED_OR_REFUND_PAID'].includes(condition)) {
          return t(REFUND_RETRIEVE_MAP[condition], {
            scene: sceneName[trigCond as keyof IScene],
          });
        }
      }

      if (scene === 'CANCEL_ORDER_RETRIEVE') {
        if (timeout) {
          return t('scene.chatWorkspace.aiReception.reminder.timeoutClose.cancelOrderRetrieve', {
            scene: sceneName[trigCond as keyof IScene],
          });
        }
        if (['NOT_IN_CANCEL_AND_NOT_CANCELLED', 'CANCELLED'].includes(condition)) {
          return t(CANCEL_ORDER_RETRIEVE_MAP[condition], {
            scene: sceneName[trigCond as keyof IScene],
          });
        }
      }

      if (scene === 'NEGATIVE_RATE_RETRIEVE') {
        if (timeout) {
          return t('scene.chatWorkspace.aiReception.reminder.timeoutClose.negativeRateRetrieve');
        }
        return t('scene.chatWorkspace.aiReception.reminder.autoClose.negativeRateRetrieve');
      }

      return '';
    },
    [t, sceneName],
  );
}
